import { Component, HostBinding, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DmpIconName } from './icon-paths.constants';
import { IconService } from './icon.service';

export type DmpIconSize = 'sm' | 'md' | 'lg';

/**
 * Reusable SVG icon — reference: icon(name, cls) helper (dmp-os-complete_01.html ~798)
 *
 * Usage:
 *   <dmp-icon name="grid" />
 *   <dmp-icon name="spark" size="sm" />
 *   <dmp-icon name="check" ariaLabel="Matched" />
 */
@Component({
    standalone: true,
    selector: 'dmp-icon',
    imports: [CommonModule],
    template: `
    <svg
      [class]="svgClasses"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      [attr.aria-hidden]="decorative ? true : null"
      [attr.aria-label]="!decorative ? ariaLabel : null"
      [attr.role]="!decorative ? 'img' : null">
      <g [innerHTML]="pathHtml"></g>
    </svg>
  `,
    changeDetection: ChangeDetectionStrategy.Eager,
    styles: [`
    :host {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      line-height: 0;
    }
    svg.icon-sm { width: 14px; height: 14px; }
    svg.icon-md { width: 16px; height: 16px; }
    svg.icon-lg { width: 20px; height: 20px; }
  `]
})
export class DmpIconComponent {
  @Input({ required: true }) name!: DmpIconName;
  @Input() size: DmpIconSize = 'md';
  /** When false, ariaLabel should be provided for screen readers */
  @Input() decorative = true;
  @Input() ariaLabel?: string;
  /** Apply global .spin animation (e.g. refresh buttons) */
  @Input() spin = false;

  @HostBinding('class.spin') get spinClass(): boolean {
    return this.spin;
  }

  constructor(private readonly iconService: IconService) {}

  get pathHtml() {
    return this.iconService.getSafePath(this.name);
  }

  get svgClasses(): string {
    return `icon-${this.size}`;
  }
}
